import { reactive, computed, onMounted } from 'vue';
import { createSharedComposable } from '@vueuse/core';

interface KbdKeysSpecificMap {
  meta: string;
  alt: string;
  ctrl: string;
}

export const kbdKeysMap = {
  meta: '',
  ctrl: '',
  alt: '',
  win: '⊞',
  command: '⌘',
  shift: '⇧',
  control: '⌃',
  option: '⌥',
  enter: '↵',
  delete: '⌦',
  backspace: '⌫',
  escape: '⎋',
  tab: '⇥',
  capslock: '⇪',
  arrowup: '↑',
  arrowright: '→',
  arrowdown: '↓',
  arrowleft: '←',
  pageup: '⇞',
  pagedown: '⇟',
  home: '↖',
  end: '↘'
};

export type KbdKey = keyof typeof kbdKeysMap;
export type KbdKeySpecific = keyof KbdKeysSpecificMap;

/**
 * Internal composable function to handle keyboard key mapping and platform detection.
 * This sets up reactive state for platform-specific keys and provides utilities for key symbol retrieval.
 * It is wrapped in `createSharedComposable` for shared state across components.
 *
 * @returns An object containing `macOS` computed property and `getKbdKey` function.
 */
const _useKbd = () => {
  const macOS = computed(
    () =>
      typeof window !== 'undefined' &&
      navigator &&
      navigator.userAgent &&
      navigator.userAgent.match(/Macintosh;/)
  );

  const kbdKeysSpecificMap = reactive({
    meta: ' ',
    alt: ' ',
    ctrl: ' '
  });

  onMounted(() => {
    kbdKeysSpecificMap.meta = macOS.value ? kbdKeysMap.command : 'Ctrl';
    kbdKeysSpecificMap.ctrl = macOS.value ? kbdKeysMap.control : 'Ctrl';
    kbdKeysSpecificMap.alt = macOS.value ? kbdKeysMap.option : 'Alt';
  });

  /**
   * Retrieves the display symbol or text for a given keyboard key.
   * Handles platform-specific keys (e.g., meta/ctrl/alt) based on detected OS,
   * falls back to predefined symbols in `kbdKeysMap`, or uppercases the input if not found.
   *
   * @param value - The key to retrieve the symbol for (optional).
   * @returns The symbol/string representation of the key, or undefined if no value provided.
   */
  function getKbdKey(value?: KbdKey | string) {
    if (!value) {
      return;
    }

    if (['meta', 'alt', 'ctrl'].includes(value)) {
      return kbdKeysSpecificMap[value as KbdKeySpecific];
    }

    return kbdKeysMap[value as KbdKey] || value.toUpperCase();
  }

  return {
    macOS,
    getKbdKey
  };
};

/**
 * Shared composable for keyboard key handling.
 * Use this in components to access platform-aware key symbols without duplicating state.
 * It detects macOS client-side and maps keys accordingly for consistent UI (e.g., in tooltips or KBD components).
 */
export const useKbd = /* @__PURE__ */ createSharedComposable(_useKbd);
